/**
 * EVERY NAME THE HELP WIDGET SHOWS OUTSIDE ITS CLOSED ROOT — plan-019 WID-CORE (ED-24; design §31.8.2, §31.8.10;
 * research `docs/research/help-widget-adblock.md` §5; ADR 0065, proposed). Contract IC-H13.
 *
 * A closed shadow root hides only what is inside it. The host element's tag and attributes, the asset file names, the
 * socket and api paths, the browser storage keys and the form marks the portal puts on its own fields are all public:
 * every content blocker sees them, and a generic list rule written for somebody else's chat launcher can hide ours. So
 * they live here, in one module, and are policed twice:
 *
 * - `naming-policy.test.ts` (this package) bans the blocker vocabulary from each of them, whatever the lists say today;
 * - plan-019 ADB's content-blocker gate (`apps/web-app/src/content-blocker/**`) imports this module
 *   (`@wetalk/help-widget/path`) and runs the same names through the vendored filter lists.
 *
 * Visible copy is not policed here: the launcher may say "Help" / «Βοήθεια» (OD-1), because text content is not what
 * generic rules match. Names *inside* the closed root are kept free of the vocabulary too (defence in depth), but they
 * are not in this module because nothing outside the root can see them.
 *
 * Imports nothing, so the gate and the hosts read it without pulling the widget in.
 */
/** The one custom element the widget adds to a page: no id, class, `aria-*`, `title`, `name` or `data-*` on it. */
export const HELP_HOST_TAG = "wt-hp";
/**
 * The paths the widget and its hosts reach. The socket's origin comes from the grant answer (`socket_url`, api
 * configuration); these are the path halves the content-blocker gate checks against the network lists. The family stays
 * `/v1/help/…` (plan-019 §0.2, settled), and no segment or query key says `track`, `collect`, `beacon`, `analytics`,
 * `feedback`, `telemetry`, `pixel`, `event` or `log` (design §2.10's note).
 */
export const HELP_PATH = Object.freeze({
    /** The help socket on the runtime's public host (`wss://rtc.wetalk.io/v1/help/socket` in production). */
    socket: "/v1/help/socket",
    /** A signed-in person's grant (portal, same origin). */
    grant: "/v1/help/grants",
    /** A signed-in person's help requests and "Your messages". */
    request: "/v1/help/requests",
    /** Whether help is on, read before the launcher or the fallback link is shown. */
    publicStatus: "/v1/public/help/status",
    /** The ALTCHA challenge for an anonymous grant or request. */
    publicChallenge: "/v1/public/help/challenge",
    /** An anonymous visitor's grant. */
    publicGrant: "/v1/public/help/grants",
    /** An anonymous visitor's help request. */
    publicRequest: "/v1/public/help/requests",
    /** The plain fallback page on each surface (OD-24): the form without the widget chunk. */
    fallbackPage: "/help",
});
/**
 * The lazily loaded panel chunk's file name in each host's build: Vite's `build.rollupOptions.output.chunkFileNames`
 * (portal) and Astro's `vite.build.rollupOptions` (wetalk.io) set it for the chunk that holds `src/panel/**`, so the
 * file is `hp.<hash>.js` — never `help-widget.js`, `chat.js` or `button.js` (research §5 rule 7).
 */
export const HELP_PANEL_CHUNK_FILE_NAME = "hp.[hash].js";
/** The stem every help asset file name starts with. */
export const HELP_ASSET_STEM = "hp";
/**
 * A chunk a page loads AT ONCE that holds some help code (this module, the anonymous solver, the host's own help code)
 * — never `hp`, so refusing the `hp` files costs only the widget (AC-HA-1, OD-24), and never the module's own name
 * (`anonymous`, `path`), which could reveal it. Chosen per chunk by `@wetalk/help-widget/chunk-name` (plan-019 E2E fix).
 */
export const HELP_SHARED_CHUNK_FILE_NAME = "shared.[hash].js";
/** Browser storage keys (design §31.8.10). No cookie is set by the widget. */
export const HELP_STORAGE_KEY = Object.freeze({
    /** `localStorage`: the anonymous visitor key, rotated after 24 hours (per-key abuse limits). */
    visitorKey: "wt-hp:k",
    /** `sessionStorage`: an anonymous conversation's resume token and socket address, this tab only. */
    resume: "wt-hp:r",
    /** `sessionStorage`: the panel was open in this tab (with `dialog`, it opens again after a reload or navigation). */
    panelOpen: "wt-hp:o",
    /**
     * `sessionStorage`: a dialog is running in this tab — a conversation with at least one message that has not ended
     * (owner, 2026-10-18: after a reload the panel stays minimized unless a dialog was running).
     */
    dialog: "wt-hp:d",
});
/** The marks the portal's fill registry puts on its own form fields (ED-25; never `data-help-*`). */
export const HELP_FORM_MARK_ATTRIBUTE = Object.freeze({
    /** The registered field code of a fillable field. */
    field: "data-wt-hp-field",
    /** `"true"` while the field still holds the value a fill proposal put there. */
    filled: "data-wt-hp-filled",
});
/**
 * The custom property a page sets (on `<html>`, inherited through the closed root) to move the launcher from its resting
 * place, the bottom inline-end corner (plan-019 INT, wave 3: wetalk.io keeps it off the landing page's phone demo).
 * Unset, the launcher rests at the corner; the panel never reads it, so an open panel stays where it opens.
 */
export const HELP_PLACEMENT_PROPERTY = Object.freeze({
    /** The launcher's distance from the viewport's block end (any CSS length; negative moves it out of view). */
    blockEnd: "--wt-hp-block-end",
});
/**
 * The words the blocker lists' maintainers use for chat launchers, feedback tabs and their kin (research §3, §5 rule 1;
 * design §31.8.2). None may appear in the host tag, a host attribute, an asset file name, a storage key, a form mark
 * or the last segment of the socket path. Compared by substring on the lowercased name, except the two-letter words
 * (`ai`, `ad`), which are compared as whole segments split on any non-letter, because `wt-hp:k` must not fail on the
 * letters of an ordinary word.
 */
export const HELP_BLOCKER_VOCABULARY = Object.freeze([
    "chat",
    "help",
    "support",
    "widget",
    "launcher",
    "assist",
    "agent",
    "bot",
    "ai",
    "feedback",
    "contact",
    "message",
    "messenger",
    "talk",
    "popup",
    "modal",
    "banner",
    "promo",
    "sponsor",
    "ad",
    "sdk",
    "button",
]);
/** Words that must stay out of every URL segment and query key the widget requests (research §5 rule 8). */
export const HELP_URL_VOCABULARY = Object.freeze([
    "track",
    "collect",
    "beacon",
    "analytics",
    "feedback",
    "telemetry",
    "pixel",
    "event",
    "log",
]);
/** The vocabulary words a name contains, by {@link HELP_BLOCKER_VOCABULARY}'s matching rule (empty when it is clean). */
export function blockerVocabularyIn(name) {
    const lowered = name.toLowerCase();
    const segments = lowered.split(/[^a-z]+/).filter((segment) => segment !== "");
    return HELP_BLOCKER_VOCABULARY.filter((word) => (word.length <= 2 ? segments.includes(word) : lowered.includes(word)));
}
//# sourceMappingURL=path.js.map